import { useEffect, useState, type ReactElement } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  routeToPath,
  useNav,
  useSearchRoute,
  type Route as PixRoute,
  type ToastInfo,
} from "./types";
import { Snackbar } from "./components/atoms";
import { GlobalHeader, MobileTabs } from "./components/Header";
import { HomePage } from "./pages/HomePage";
import { WorkPage } from "./pages/WorkPage";
import { AuthorPage } from "./pages/AuthorPage";
import { SearchPage } from "./pages/SearchPage";
import { LibraryPage } from "./pages/LibraryPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AuthPage } from "./pages/AuthPage";
import { AuthProvider, useAuth } from "./auth";
import { I18nProvider, useT } from "./i18n";
import type { AuthUser } from "./api";

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

function Shell() {
  const [toast, setToast] = useState<ToastInfo | null>(null);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const nav = useNav();
  const { user, setUser, logout: authLogout } = useAuth();
  const t = useT();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  function handleLogin(u: AuthUser) {
    setUser(u);
    setToast({ kind: "success", text: t("auth.welcome", { name: u.displayName }) });
  }
  function handleLogout() {
    authLogout();
    setToast({ kind: "info", text: t("auth.loggedOut") });
    nav({ page: "home" });
  }

  const isLoggedIn = !!user;

  return (
    <>
      <Routes>
        <Route path="/login" element={<AuthRoute mode="login" onLogin={handleLogin} />} />
        <Route
          path="/signup"
          element={<AuthRoute mode="signup" onLogin={handleLogin} />}
        />

        <Route
          element={
            <MainLayout
              query={query}
              setQuery={setQuery}
              isLoggedIn={isLoggedIn}
              onLogout={handleLogout}
            />
          }
        >
          <Route path="/" element={<HomeRoute />} />
          <Route path="/works/:id" element={<WorkRoute setToast={setToast} />} />
          <Route path="/users/:id" element={<AuthorRoute />} />
          <Route path="/search" element={<SearchRoute query={query} setQuery={setQuery} />} />
          <Route
            path="/library"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <LibraryRoute setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <DashboardRoute setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      {toast && (
        <Snackbar kind={toast.kind} onClose={() => setToast(null)}>
          {toast.text}
        </Snackbar>
      )}
    </>
  );
}

function MainLayout({
  query,
  setQuery,
  isLoggedIn,
  onLogout,
}: {
  query: string;
  setQuery: (q: string) => void;
  isLoggedIn: boolean;
  onLogout: () => void;
}) {
  const location = useLocation();
  const route = pathToPixRoute(location.pathname, location.search);
  const nav = useNav();
  return (
    <div className="app">
      <GlobalHeader
        route={route}
        nav={nav}
        query={query}
        setQuery={setQuery}
        isLoggedIn={isLoggedIn}
        onLogout={onLogout}
      />
      <Outlet />
      <div className="mobile-only">
        <MobileTabs route={route} nav={nav} />
      </div>
    </div>
  );
}

function HomeRoute(): ReactElement {
  const nav = useNav();
  return <HomePage nav={nav} />;
}

function WorkRoute({ setToast }: { setToast: (t: ToastInfo) => void }): ReactElement {
  const { id } = useParams<"id">();
  const nav = useNav();
  return <WorkPage workId={id ?? ""} nav={nav} onToast={setToast} />;
}

function AuthorRoute(): ReactElement {
  const { id } = useParams<"id">();
  const nav = useNav();
  const { user } = useAuth();
  const authorId = !id || id === "me" ? user?.id ?? "" : id;
  return <AuthorPage authorId={authorId} nav={nav} />;
}

function SearchRoute({
  query,
  setQuery,
}: {
  query: string;
  setQuery: (q: string) => void;
}): ReactElement {
  const route = useSearchRoute();
  const nav = useNav();
  useEffect(() => {
    if (route.query !== undefined && route.query !== query) setQuery(route.query);
  }, [route.query, query, setQuery]);
  return <SearchPage route={route} nav={nav} query={query} setQuery={setQuery} />;
}

function LibraryRoute({ setToast }: { setToast: (t: ToastInfo) => void }): ReactElement {
  const nav = useNav();
  return <LibraryPage nav={nav} onToast={setToast} />;
}

function DashboardRoute({ setToast }: { setToast: (t: ToastInfo) => void }): ReactElement {
  const nav = useNav();
  return <DashboardPage nav={nav} onToast={setToast} />;
}

function AuthRoute({
  mode,
  onLogin,
}: {
  mode: "login" | "signup";
  onLogin: (u: AuthUser) => void;
}): ReactElement {
  const nav = useNav();
  return (
    <AuthPage route={{ page: "auth", mode }} nav={nav} onLogin={onLogin} />
  );
}

function ProtectedRoute({
  isLoggedIn,
  children,
}: {
  isLoggedIn: boolean;
  children: React.ReactNode;
}) {
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function pathToPixRoute(pathname: string, search: string): PixRoute {
  if (pathname === "/" || pathname === "") return { page: "home" };
  if (pathname.startsWith("/works/"))
    return { page: "work", id: decodeURIComponent(pathname.slice(7)) };
  if (pathname.startsWith("/users/")) {
    const id = decodeURIComponent(pathname.slice(7));
    return { page: "author", id: id === "me" ? undefined : id };
  }
  if (pathname === "/search") {
    const sp = new URLSearchParams(search);
    return {
      page: "search",
      ...(sp.get("q") ? { query: sp.get("q")! } : {}),
    };
  }
  if (pathname === "/library") return { page: "library" };
  if (pathname === "/dashboard") return { page: "dashboard" };
  if (pathname === "/login") return { page: "auth", mode: "login" };
  if (pathname === "/signup") return { page: "auth", mode: "signup" };
  return { page: "home" };
}

export { routeToPath };
